
import { fetchEventDetails } from '@/api/Events'
import { EventDetails } from '@/api/Helpers'
import { getEventPayload, triggerViewItemEvent } from '@/checkout/helpers/ga4'
import CartWidget from '@/components/cart/CartWidget.vue'
import FloatingNavigateBack from '@/components/elements/FloatingNavigateBack.vue'
import MembersBanner from '@/components/elements/MembersBanner.vue'
import Subtitle from '@/components/elements/Subtitle.vue'
import TenantContent from '@/components/elements/TenantContent'
import AddOneToCart from '@/components/events/AddOneToCart.vue'
import BuyMembership from '@/components/events/BuyMembership.vue'
import ReserveDateFirst from '@/components/events/ReserveDateFirst.vue'
import ReserveQuantityFirst from '@/components/events/ReserveQuantityFirst.vue'
import ReserveSingleEvent from '@/components/events/ReserveSingleEvent.vue'
import MembersOnly from '@/components/membership/MembersOnly.vue'
import { isAnchor, isUpsell } from '@/helpers/Anchors'
import environment, { portal } from '@/helpers/Environment'
import { coverImageUrl } from '@/helpers/ImageUrls'
import { languageItem } from '@/helpers/LanguageHelpers'
import { openDescriptionModal } from '@/helpers/ModalDialogs'
import { getSummary, isTrueish, toKebabCase } from '@/helpers/StringHelpers'
import tenant from '@/helpers/Tenant'
import { isForMembersOnly } from '@/helpers/TicketGroupHelpers'
import { currentMembership, isCurrentMember } from '@/state/Membership'
import store from '@/store/store'
import { ViewItemPayload } from '@/types/ga4'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { RawLocation, Route } from 'vue-router'

function loader(to: Route, from, next): Promise<EventDetails | void> {
  return fetchEventDetails(to.params.id, to.query.tg).catch((e) => {
    if (e.notFoundError) {
      // The `fullPath` gets URL-encoded, but at least it includes the search/query string from the original URL.
      // TODO Work out how to pass on the search/query string without encoding it.
      // TODO Find documentation for params array. Only the object usage of it is documented.
      // @see https://router.vuejs.org/guide/essentials/navigation.html
      next({ name: 'error/404', params: [to.fullPath], replace: true })
    } else {
      throw e
    }
  })
}

/**
 * Handles /event/:id route.
 *
 * Purchase flow flowcharts are in Google Docs.
 * @see https://drive.google.com/file/d/1rPlkpO5Fz5t7JL19AEqzZTfzqGzUdbmg/view?usp=sharing
 */
@Component({
  name: 'EventDetailRoute',
  components: {
    MembersBanner,
    BuyMembership,
    ReserveDateFirst,
    ReserveSingleEvent,
    ReserveQuantityFirst,
    AddOneToCart,
    TenantContent,
    MembersOnly,
    CartWidget,
    Subtitle,
    FloatingNavigateBack,
  },
})
export default class extends Vue {
  @Prop({ required: true })
  id: string

  @Prop()
  action: string

  loading = true
  event: EventDetails | null = null

  mounted() {
    window.addEventListener('scroll', this.scrollHandler)
  }

  destroyed() {
    window.removeEventListener('scroll', this.scrollHandler)
  }

  private scrollHandler() {
    const imgEl: HTMLElement = this.$refs['mobile-image'] as HTMLElement
    // Parallax effect
    const factor = 3
    if (imgEl) {
      imgEl.style.top = `${window.scrollY / factor}px`
    }
  }

  beforeRouteEnter(to, from, next) {
    loader(to, from, next).then((event) => {
      next((vm) => vm.handleEventDetailsResponse(event))
    })
  }

  beforeRouteUpdate(to, from, next) {
    this.loading = true
    loader(to, from, next).then((event) => {
      this.handleEventDetailsResponse(event)
      next()
    })
  }

  handleEventDetailsResponse(response: EventDetails | void) {
    if (response) {
      this.event = response
      this.appendPageTitle(this.event.name)
      this.loading = false

      const payload: ViewItemPayload = {
        currency: portal.default_currency_code,
        value: 0,
        items: [getEventPayload(this.event)],
      }
      triggerViewItemEvent(payload)
    }
  }

  get categoryClass() {
    return this.event && toKebabCase(this.event.category)
  }

  get imageUrl() {
    return coverImageUrl(this.event)
  }

  get imageAltText() {
    return this.event!.meta.image_alt_text ?? `Image for ${this.event!.name}`
  }

  get membersOnly() {
    const event = this.event!
    return isForMembersOnly(event) || event.ticketGroups.every(isForMembersOnly)
  }

  get user() {
    return store.getters['Member/user']
  }

  get normalizedCategory(): string {
    return this.event!.category.toLowerCase().replace(' ', '')
  }

  get contexts(): Set<string> {
    const result = new Set<string>()

    // @see https://docs.google.com/document/d/10ek7SBc9vb-fX3eQVHFthaA1sFdEOmHEs0oOgHIodsY/edit
    this.event!.ticketGroups.forEach((tg) => result.add('handler:' + tg.handler))

    const oneOnly = this.event!.ticketGroups.every((tg) => tg.handler === 'ledger' || tg.handler === 'donation')
    if (oneOnly) {
      result.add('one-ticket-only')
    }

    if (isAnchor(this.event!)) {
      result.add('anchor')
    }

    if (this.normalizedCategory === 'membership') {
      if (this.event!.ticketGroups.some((tg) => tg.handler === 'membership')) {
        result.add('membership')

        if (isCurrentMember() || this.action === 'renew') {
          result.add('renew')
        }

        if (this.action === 'gift') {
          result.add('gift')
        }
      }

      if (this.event!.ticketGroups.every((tg) => tg.handler === 'codes')) {
        result.add('gift-of-membership')
      }
    }

    return result
  }

  getContextualTitle(): string | void {
    const titles = languageItem('contextualEventTitles')

    if (this.contexts.has('anchor')) {
      return titles.anchor
    } else if (this.contexts.has('handler:ledger')) {
      return titles.giftCard
    } else if (this.contexts.has('handler:donation')) {
      return titles.donation
    } else if (this.contexts.has('gift')) {
      return titles.membershipGift
    } else if (this.contexts.has('renew')) {
      return titles.membershipRenew
    } else if (this.contexts.has('membership')) {
      // Use the membership title only if gift and renew contexts are absent.
      return titles.membership
    }
  }

  get title(): string {
    return this.getContextualTitle() || this.event!.name
  }

  get titleInsideContentColumn() {
    if (this.getContextualTitle()) {
      return tenant.titleInsideContentColumn
    } else {
      return this.opt.eventNameTitlePosition === 'inside content' || tenant.titleInsideContentColumn
    }
  }

  onReserveSuccess() {
    this.$router.push(this.nextRoute)
  }

  get nextRoute(): RawLocation {
    return environment.web.upsells ? '/events' : '/checkout'
  }

  get hideEventDetails() {
    return isTrueish(this.event!.meta.hide_event_details)
  }

  get anchorRequired(): boolean {
    // Does the cart already have an anchor event in it?
    return isUpsell(this.event!) && store.getters['Cart/anchor'] == null
  }

  get isMembersBannerEnabled() {
    return !this.contexts.has('membership')
  }

  get quantityFirst(): boolean {
    const config = this.event!.meta.reserve_flow ?? environment.config.default_reserve_flow
    return config === 'quantity_first'
  }

  get isMembershipEvent() {
    return this.contexts.has('membership')
  }

  get currentMembership() {
    return currentMembership()
  }

  get promo(): string | null {
    const template = this.$portalString.event_promo
    if (template && template.length > 0) {
      return template
    } else {
      return null
    }
  }

  get membershipPromo(): string | null {
    const currentPath = this.$router.currentRoute.fullPath
    const baseMembershipRoute = `/events/${environment.web.membership_event_id}`
    const template = this.$portalString.event_membership_promo

    if (!isCurrentMember() && !currentPath.startsWith(baseMembershipRoute) && template && template.length > 0) {
      return template
    } else {
      return null
    }
  }

  get eventSummary(): string {
    return getSummary(this.event!.description, this.event!.summary) || ''
  }

  openDescriptionModal() {
    openDescriptionModal(this.event!.description)
  }
}
